import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetProfileDefinitionsByTopic } from "../../../../api/queries";
import {
  useActivateProfileDefinition,
  useDeleteProfileDefinition,
  useUpdateProfileDefinition,
  useReorderProfileDefinitions,
} from "../../../../api/mutations";
import { useNotification } from "../../../../hooks/useNotification";
import ConfirmationBox from "../../../../componentLibrary/ConfirmationBox";
import RenameModal from "../../../../componentLibrary/RenameModal";
import Spinner from "../../../../componentLibrary/Spinner";
import type { ToolboxProfileDefinitionI } from "../../../../types";
import ReorderableVerticalList from "../../../../componentLibrary/ReorderableVerticalList";
import RbdFullRowDragSurface from "../../../../componentLibrary/RbdFullRowDragSurface";
import { DefinitionRow } from "./DefinitionRow";

interface Props {
  topicId: string;
  topicTitle: string;
}

export default function ProfileDefinitionEditor({
  topicId,
  topicTitle,
}: Props) {
  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const [activateTarget, setActivateTarget] =
    useState<ToolboxProfileDefinitionI | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<ToolboxProfileDefinitionI | null>(null);
  const [renameTarget, setRenameTarget] =
    useState<ToolboxProfileDefinitionI | null>(null);
  const [orderedDefinitions, setOrderedDefinitions] = useState<
    ToolboxProfileDefinitionI[] | null
  >(null);

  const {
    data: definitions,
    isLoading,
    isError,
  } = useGetProfileDefinitionsByTopic(topicId);

  useEffect(() => {
    if (definitions) {
      setOrderedDefinitions([...definitions]);
    }
  }, [definitions]);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["toolboxProfileDefinitions", topicId],
    });

  const activateMutation = useActivateProfileDefinition({
    onSuccess: async () => {
      notify("success", "Profile definition activated");
      setActivateTarget(null);
      await invalidate();
    },
    onError: () => notify("error", "Failed to activate profile definition"),
  });

  const deleteMutation = useDeleteProfileDefinition({
    onSuccess: async () => {
      notify("success", "Profile definition deleted");
      setDeleteTarget(null);
      await invalidate();
    },
    onError: () => notify("error", "Failed to delete profile definition"),
  });

  const renameMutation = useUpdateProfileDefinition({
    onSuccess: async () => {
      notify("success", "Definition name updated");
      setRenameTarget(null);
      await invalidate();
      await queryClient.invalidateQueries({
        queryKey: ["toolboxProfileDefinition"],
      });
    },
    onError: () => notify("error", "Failed to update name"),
  });

  const reorderMutation = useReorderProfileDefinitions({
    onSuccess: async () => {
      notify("success", "Definition order saved");
      await invalidate();
    },
    onError: () => {
      notify("error", "Failed to save definition order");
      void queryClient.invalidateQueries({
        queryKey: ["toolboxProfileDefinitions", topicId],
      });
    },
  });

  const definitionList = orderedDefinitions ?? definitions ?? [];

  const handleReorderDefinitions = (next: ToolboxProfileDefinitionI[]) => {
    setOrderedDefinitions(next);
    reorderMutation.mutate({
      topicId,
      orderedDefinitionIds: next.map((d) => d._id),
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner className="w-6" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-salmon-400 py-2">
        Failed to load profile definitions.
      </p>
    );
  }

  const defLabel = (def: ToolboxProfileDefinitionI) =>
    def.displayName?.trim() || `v${def.version}`;

  return (
    <div className="space-y-3 mt-6">
      {definitions?.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">
          No definition versions yet. Create one to start building the{" "}
          {topicTitle} profile.
        </p>
      ) : (
        <ReorderableVerticalList
          items={definitionList}
          getId={(d) => d._id}
          onReorder={handleReorderDefinitions}
          droppableId="toolbox-profile-definition-versions"
          isDragDisabled={reorderMutation.isPending}
          className="space-y-2"
        >
          {(def, _i, provided) => (
            <RbdFullRowDragSurface provided={provided}>
              <DefinitionRow
                definition={def}
                editPath={`/admin/toolboxes/${topicId}/profile-definitions/${def._id}/edit`}
                onActivate={() => setActivateTarget(def)}
                onDelete={() => setDeleteTarget(def)}
                onRename={() => setRenameTarget(def)}
                activating={
                  activateMutation.isPending && activateTarget?._id === def._id
                }
                deleting={
                  deleteMutation.isPending && deleteTarget?._id === def._id
                }
              />
            </RbdFullRowDragSurface>
          )}
        </ReorderableVerticalList>
      )}

      <RenameModal
        isOpen={!!renameTarget}
        title="Rename definition"
        subtitle={
          renameTarget
            ? `Leave blank to use the default label (v${renameTarget.version}).`
            : undefined
        }
        initialValue={renameTarget?.displayName ?? ""}
        placeholder={`e.g. Spring rollout · v${renameTarget?.version ?? "1"}`}
        loading={renameMutation.isPending}
        onClose={() => {
          if (!renameMutation.isPending) setRenameTarget(null);
        }}
        onSave={(trimmed) => {
          if (!renameTarget) return;
          renameMutation.mutate({
            definitionId: renameTarget._id,
            displayName: trimmed.length > 0 ? trimmed : null,
          });
        }}
      />

      <ConfirmationBox
        isOpen={!!activateTarget}
        title="Activate this definition?"
        message={`${activateTarget ? defLabel(activateTarget) : ""} will become the live profile definition for all users.`}
        confirmText="Activate"
        cancelText="Cancel"
        onConfirm={() =>
          activateTarget && activateMutation.mutate(activateTarget._id)
        }
        onCancel={() => setActivateTarget(null)}
        onCloseBox={() => setActivateTarget(null)}
      />

      <ConfirmationBox
        isOpen={!!deleteTarget}
        title="Delete this definition?"
        message={`${deleteTarget ? defLabel(deleteTarget) : ""} will be permanently deleted. This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        danger
        onConfirm={() =>
          deleteTarget && deleteMutation.mutate(deleteTarget._id)
        }
        onCancel={() => setDeleteTarget(null)}
        onCloseBox={() => setDeleteTarget(null)}
      />
    </div>
  );
}
